import type { JourneyDefinition } from "@/lib/journey-api";
import { NEEDS_AI_DRAFT_BODY } from "@/lib/journey-touch-display";
import type { WecomInboundEvent } from "@/lib/wecom-inbound-events";
import type { WecomOutboundMessage } from "@/lib/wecom-outbound-messages";

/** 聊天时间线中的旅程计划步（真实绑定计划，或旧预览投影） */
export type JourneyPlanItem = {
  id: string;
  journeyId: number;
  journeyName: string;
  /** 客户正文或占位短句（勿把手册当正文） */
  body: string;
  /** 手册节点执行说明 / 计划意图（运营看「这一步要干什么」） */
  instruction?: string | null;
  offsetDays: number;
  /** 计划发送时间 ISO */
  scheduledAt: string;
  /** 真实计划步状态；预览可无 */
  stepStatus?: string;
  /** 计划步序号（1-based） */
  position?: number;
  /** 真实 PlanStep.id；预览无 */
  planStepId?: number;
  /** true = 未绑定客户时的节点投影预览 */
  preview?: boolean;
  /** true = LLM 占位稿，未经 AI 生成客户文案 */
  needsAiDraft?: boolean;
  /** 手册节点 id；标题用手册序而非 position */
  journeyNodeId?: number | null;
  /** 手册序 1-based */
  handbookOrdinal?: number | null;
};

export type ChatTimelineItem =
  | { kind: "inbound"; at: number; event: WecomInboundEvent }
  | { kind: "outbound"; at: number; message: WecomOutboundMessage }
  | { kind: "plan"; at: number; plan: JourneyPlanItem };

function eventTimeMs(event: WecomInboundEvent): number {
  const iso = event.wecom_created_at || event.created_at;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : 0;
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * 将已启用旅程的节点投影为未来计划（仅预览；真实客户请用 binding plan）。
 * 假设触发日=今天，按 offset_days 排期。
 */
export function projectFutureJourneyPlans(
  journeys: JourneyDefinition[],
  now: Date = new Date(),
): JourneyPlanItem[] {
  const day0 = startOfLocalDay(now);
  const items: JourneyPlanItem[] = [];

  for (const journey of journeys) {
    if (journey.status !== "enabled") continue;
    for (const node of journey.nodes ?? []) {
      const offset = Number(node.offset_days) || 0;
      const scheduled = new Date(day0);
      scheduled.setDate(scheduled.getDate() + offset);
      const instruction =
        (node.instruction ?? "").trim() ||
        (node.prompt ?? "").trim() ||
        "";
      const nodeKey =
        node.id != null
          ? String(node.id)
          : `d${offset}-${instruction.slice(0, 12) || "node"}`;
      items.push({
        id: `plan-${journey.id}-${nodeKey}`,
        journeyId: journey.id,
        journeyName: journey.name,
        // 预览尚无客户稿：消息位占位，任务区放手册
        body: NEEDS_AI_DRAFT_BODY,
        instruction: instruction || null,
        offsetDays: offset,
        scheduledAt: scheduled.toISOString(),
        preview: true,
        needsAiDraft: true,
      });
    }
  }

  return items.sort((a, b) => {
    const diff = Date.parse(a.scheduledAt) - Date.parse(b.scheduledAt);
    if (diff !== 0) return diff;
    return a.id.localeCompare(b.id);
  });
}

/** 默认预览条数：最近 2 条未来计划 */
export const DEFAULT_FUTURE_PLAN_PREVIEW = 2;

/**
 * 开关语义：
 * - showAllFuture=false（默认）：最多展示 2 条
 * - showAllFuture=true：展示全部未来计划
 */
export function selectFuturePlans(
  plans: JourneyPlanItem[],
  showAllFuture: boolean,
  previewCount: number = DEFAULT_FUTURE_PLAN_PREVIEW,
): JourneyPlanItem[] {
  if (showAllFuture) return plans;
  return plans.slice(0, previewCount);
}

/**
 * 合并入站 / 外发 / 未来计划，按时间升序（旧在上、新在下；计划排在消息之后按计划时间）。
 * API 入站默认可为 newest_first，此处统一 chrono。
 */
export function buildChatTimelineItems(opts: {
  inboundEvents: WecomInboundEvent[];
  outboundMessages: WecomOutboundMessage[];
  futurePlans: JourneyPlanItem[];
}): ChatTimelineItem[] {
  const items: ChatTimelineItem[] = [];

  for (const event of opts.inboundEvents) {
    items.push({ kind: "inbound", at: eventTimeMs(event), event });
  }
  for (const message of opts.outboundMessages) {
    const ms = Date.parse(message.created_at);
    items.push({
      kind: "outbound",
      at: Number.isFinite(ms) ? ms : 0,
      message,
    });
  }
  for (const plan of opts.futurePlans) {
    const ms = Date.parse(plan.scheduledAt);
    items.push({
      kind: "plan",
      at: Number.isFinite(ms) ? ms : 0,
      plan,
    });
  }

  return items.sort((a, b) => {
    if (a.at !== b.at) return a.at - b.at;
    // 同时刻：入站 → 外发 → 计划
    const rank = { inbound: 0, outbound: 1, plan: 2 } as const;
    return rank[a.kind] - rank[b.kind];
  });
}
